import React from 'react';
import { Space, DatePicker } from 'antd';
import { Button } from '@mui/material';
import { ReactComponent as DropdownArrow } from "../../../assets/images/dropdown-arrow.svg";
import dayjs from 'dayjs';
import { useSelector } from 'react-redux';

const { RangePicker } = DatePicker;

const DateRangeSelector = ({ dateRange, onDateChange, textStyle, ignoreJoiningDate = false, open: controlledOpen, onOpenChange: onControlledOpenChange, variant = 'analytics' }) => {
  const isSearch = variant === 'search';
  const [internalOpen, setInternalOpen] = React.useState(false);
  const isControlled = controlledOpen !== undefined;
  const panelVisible = isControlled ? controlledOpen : internalOpen;
  const setPanelVisible = React.useCallback((next) => {
    if (!isControlled) setInternalOpen(next);
    onControlledOpenChange?.(next);
  }, [isControlled, onControlledOpenChange]);
  const userData = useSelector((state) => state.loginToken);

  const today = dayjs().startOf('day');

  const joiningDate = React.useMemo(() => {
    const parsed = dayjs(userData?.response?.user?.joining_date);
    if (parsed.isValid())
    return parsed.startOf('day');
    if (ignoreJoiningDate)
    return dayjs('2000-01-01').startOf('day');
    return dayjs('1900-01-01').startOf('day');
  }, [userData?.response?.user?.joining_date, ignoreJoiningDate]);

  const tenureDays = today.diff(joiningDate, 'day');

  const defaultRangeWhenEmpty = React.useMemo(() => {
    const now = dayjs().startOf('day');
    if (ignoreJoiningDate) {
      return [now.startOf('month'), now];
    }
    return [joiningDate, now];
  }, [ignoreJoiningDate, joiningDate]);

  const dates = React.useMemo(() => {
    const start = dayjs(dateRange?.startDate);
    const end = dayjs(dateRange?.endDate);

    if (!start.isValid() || !end.isValid() || end.isBefore(start)) {
      return defaultRangeWhenEmpty;
    }

    const clampedStart = start.isBefore(joiningDate) ? joiningDate : start.isAfter(today) ? today : start;
    const clampedEnd = end.isAfter(today) ? today : end.isBefore(joiningDate) ? joiningDate : end;
    return [clampedStart, clampedEnd];
  }, [dateRange, joiningDate, today, defaultRangeWhenEmpty]);

  const handleDateChange = React.useCallback((newDates) => {
    if (onDateChange && newDates?.[0]?.isValid() && newDates?.[1]?.isValid()) {
      const start = newDates[0];
      const end = newDates[1];
      const today = dayjs().startOf('day');

      const rawLast7Start = today.subtract(6, 'day');
      const clampedLast7Start = rawLast7Start.isBefore(joiningDate) ? joiningDate : rawLast7Start;
      if (start.isSame(clampedLast7Start, 'day') && end.isSame(today, 'day')) {
        onDateChange({
          startDate: rawLast7Start.format('YYYY-MM-DD'),
          endDate: today.format('YYYY-MM-DD')
        });
        return;
      }

      const rawLast30Start = today.subtract(29, 'day');
      const clampedLast30Start = rawLast30Start.isBefore(joiningDate) ? joiningDate : rawLast30Start;
      if (start.isSame(clampedLast30Start, 'day') && end.isSame(today, 'day')) {
        onDateChange({
          startDate: rawLast30Start.format('YYYY-MM-DD'),
          endDate: today.format('YYYY-MM-DD')
        });
        return;
      }

      const rawLast12Start = today.subtract(12, 'month').add(1, 'day');
      const clampedLast12Start = rawLast12Start.isBefore(joiningDate) ? joiningDate : rawLast12Start;
      if (start.isSame(clampedLast12Start, 'day') && end.isSame(today, 'day')) {
        onDateChange({
          startDate: rawLast12Start.format('YYYY-MM-DD'),
          endDate: today.format('YYYY-MM-DD')
        });
        return;
      }

      onDateChange({
        startDate: start.format('YYYY-MM-DD'),
        endDate: end.format('YYYY-MM-DD')
      });
    }
  }, [onDateChange, joiningDate]);

  const getDateRangeLabel = React.useCallback(() => {
    const start = dates[0];
    const end = dates[1];
    const hasValidRange = dateRange?.startDate && dateRange?.endDate && dayjs(dateRange.startDate).isValid() && dayjs(dateRange.endDate).isValid();
    if (isSearch && !hasValidRange) return 'Select date range';

    const today = dayjs().startOf('day');
    if (isSearch) return `${start.format('MMM DD, YYYY')} - ${end.format('MMM DD, YYYY')}`;

    const yesterday = today.subtract(1, 'day');
    if (start.isSame(yesterday, 'day') && end.isSame(yesterday, 'day')) return 'Yesterday';
    if (start.isSame(today, 'day') && end.isSame(today, 'day')) return 'Today';
    const last7Start = today.subtract(6, 'day');
    const clampedLast7Start = last7Start.isBefore(joiningDate) ? joiningDate : last7Start;
    if (start.isSame(clampedLast7Start, 'day') && end.isSame(today, 'day')) return 'Last 7 Days';
    const last30Start = today.subtract(29, 'day');
    const clampedLast30Start = last30Start.isBefore(joiningDate) ? joiningDate : last30Start;
    if (start.isSame(clampedLast30Start, 'day') && end.isSame(today, 'day')) return 'Last 30 Days';
    const last12Start = today.subtract(12, 'month').add(1, 'day');
    const clampedLast12Start = last12Start.isBefore(joiningDate) ? joiningDate : last12Start;
    if (start.isSame(clampedLast12Start, 'day') && end.isSame(today, 'day')) return 'Last 12 Months';
    return `${start.format('MMM DD, YYYY')} - ${end.format('MMM DD, YYYY')}`;
  }, [dates, joiningDate, isSearch, dateRange?.startDate, dateRange?.endDate]);

  // Preset options - All shown, but disabled based on tenure conditions
  const rangePresets = React.useMemo(() => {
    const today = dayjs().startOf('day');
    const presets = [];

    // Helper to create disabled label
    const createDisabledLabel = (labelText) => (
      <span
        style={{
          opacity: 0.5,
          cursor: 'not-allowed',
          color: '#9CA3AF',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {labelText}
      </span>
    );

    // Today - always enabled
    presets.push({
      label: 'Today',
      value: [today, today]
    });

    // Yesterday - enabled if yesterday >= joiningDate (tenure >= 1)
    const yesterdayRaw = today.subtract(1, 'day');
    const isYesterdayEnabled = !yesterdayRaw.isBefore(joiningDate);
    presets.push({
      label: isYesterdayEnabled ? 'Yesterday' : createDisabledLabel('Yesterday'),
      value: [yesterdayRaw, yesterdayRaw]
    });

    // Last 7 Days - always enabled, clamped if necessary
    const last7Raw = today.subtract(6, 'day');
    const last7Clamped = last7Raw.isBefore(joiningDate) ? joiningDate : last7Raw;
    presets.push({
      label: 'Last 7 Days',
      value: [last7Clamped, today]
    });

    // Last 30 Days - enabled if tenure > 1 week (tenureDays > 7), clamped if necessary
    const last30Raw = today.subtract(29, 'day');
    const isLast30Enabled = tenureDays >= 7;
    const last30Clamped = last30Raw.isBefore(joiningDate) ? joiningDate : last30Raw;
    presets.push({
      label: isLast30Enabled ? 'Last 30 Days' : createDisabledLabel('Last 30 Days'),
      value: [last30Clamped, today]
    });

    // Last 12 Months - enabled if tenure > 1 month (tenureDays > 30), clamped if necessary
    const last12Raw = today.subtract(12, 'month').add(1, 'day');
    const isLast12Enabled = tenureDays >= 30;
    const last12Clamped = last12Raw.isBefore(joiningDate) ? joiningDate : last12Raw;
    presets.push({
      label: isLast12Enabled ? 'Last 12 Months' : createDisabledLabel('Last 12 Months'),
      value: [last12Clamped, today]
    });

    return presets;
  }, [joiningDate, tenureDays]);

  // Disable dates outside valid range
  const disabledDate = React.useCallback((current) => {
    const todayEnd = dayjs().endOf('day');
    return current && (current < joiningDate || current > todayEnd);
  }, [joiningDate]);

  return (
    <>
      <div style={{ position: 'relative' }}>
        <Button
          variant="outlined"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setPanelVisible(true);
          }}
          sx={{
            textTransform: 'none',
            fontSize: '14px',
            color: isSearch ? '#405261' : '#374151',
            borderColor: isSearch ? '#D5DCE1' : '#D1D5DB',
            minWidth: isSearch ? 160 : 136,
            height: isSearch ? '32px' : '40px',
            fontFamily: 'Inter',
            borderRadius: '8px',
            whiteSpace: 'nowrap',
            padding: isSearch ? '0 12px' : undefined,
            boxShadow: isSearch ? '0px 1px 2px 0px rgba(6, 21, 34, 0.05)' : undefined,
            bgcolor: '#FFF',
            '&:hover': {
              borderColor: isSearch ? '#C5CED6' : '#9CA3AF',
              bgcolor: '#FFF',
            },
          }}
        >
          <Space style={{ display: 'flex', alignItems: 'center' }}>
            <span style={textStyle}>{getDateRangeLabel()}</span>
            <DropdownArrow style={{
              width: 16,
              height: 16,
              color: isSearch ? '#405261' : '#374151',
              display: 'flex',
              alignItems: 'center'
            }} />
          </Space>
        </Button>

        <RangePicker
          open={panelVisible}
          presets={isSearch ? [] : rangePresets}
          value={dates}
          disabledDate={disabledDate}
          allowClear={false}
          getPopupContainer={() => document.body}
          dropdownAlign={{
            points: ['tl', 'bl'],
            offset: [0, 4],
            overflow: {
              adjustX: true,
              adjustY: false
            }
          }}
          popupStyle={{
            maxHeight: '400px',
            overflowY: 'auto'
          }}
          style={{
            pointerEvents: 'none',
            opacity: 0,
            position: 'absolute',
            top: 0,
            left: 0,
          }}
          onChange={(ranges) => {
            // Save dates but DON'T close picker - let user keep adjusting
            if (ranges && ranges[0] && ranges[1]) {
              handleDateChange(ranges);
            }
          }}
          onOpenChange={(open) => {
            setPanelVisible(open);
          }}
        />
      </div>

      <style jsx global>{`
        .ant-picker-dropdown {
          z-index: 9999 !important;
        }

        @media (max-width: 900px) {
          .ant-picker-dropdown {
            position: fixed !important;
            left: 63.5% !important;
            transform: translateX(-50%) !important;
            max-width: calc(100vw - 32px) !important;
            width: auto !important;
          }

          .ant-picker-panel-container {
            max-width: 100% !important;
          }

          .ant-picker-panels {
            flex-direction: column !important;
          }

          .ant-picker-panel {
            width: 100% !important;
            max-width: 320px !important;
          }

          .ant-picker-date-panel,
          .ant-picker-body {
            width: 100% !important;
          }
        }

        .ant-picker-presets ul {
          padding: 8px !important;
        }

        .ant-picker-presets li {
          color: #556575 !important;
          font-family: Inter !important;
          font-size: 14px !important;
          font-weight: 400 !important;
          padding: 8px 16px !important;
          border-radius: 8px !important;
          margin-bottom: 4px !important;
        }

        .ant-picker-presets li:hover {
          color: #061522 !important;
          background: #F9FAFA !important;
        }

        .ant-picker-presets li:last-child {
          margin-bottom: 0 !important;
        }
      `}</style>
    </>
  );
};

export default DateRangeSelector;